/**
 * The browse canvas's selection, as TWO INDEPENDENT ATOMS.
 *
 * ★ THE UNION IS GONE, AND THAT IS THE FIX (obs. 5, round 2). Until this file
 * was rewritten, "which card is the pointer on" and "which pin's preview is
 * open" were ONE `ListingSelection` union with an `origin` discriminant, so
 * every hover was a statement about the preview whether it meant to be one or
 * not. `hoverSelection` was the arbitration that fact forced: a hover-IN
 * REPLACED a marker selection (the popover vanished), a hover-OUT did not. It
 * was well documented, tested in both directions, and it is the reason the
 * drawer could be stranded:
 *
 *     desktop -> click a pin  (selection = marker, and the drawer's reducer is
 *                              driven to `hiddenForPreview` for a drawer that
 *                              is not even mounted at this width)
 *             -> hover a card (selection = hover - the marker is GONE, the
 *                              popover closes, and nothing dispatches
 *                              `previewDismissed`, because only the dismiss
 *                              button did)
 *             -> shrink below `md`
 *                             (the drawer mounts `open=false`, with no preview
 *                              on screen and nothing that can bring it back)
 *
 * Two facts sharing one slot is what let a hover erase a preview; a STORED
 * `hiddenForPreview` is what let the erasure outlive the thing that caused it.
 * Two atoms and a DERIVED openness remove both halves: there is no arbitration
 * to get wrong, and nothing left to go stale.
 *
 * AND IT SETTLES THE UX QUESTION BY CONSTRUCTION. The old behaviour - a hover
 * dismisses an open popover - had a test calling it deliberate. It is not what
 * Airbnb or Zillow do: a popup survives until an explicit dismiss or a click
 * elsewhere, because a pointer crossing the list on its way somewhere is not a
 * decision. With the two facts separate, "hover never dismisses" is not a rule
 * anything has to remember; it is the absence of a coupling.
 */

import type { ListingCardData } from '@/shared/lib/types/listing-card';
import type { ListingId } from '@letsgethome/types/branded';

/**
 * The pin the MAP styles as selected.
 *
 * The precedence is not cosmetic and not free to choose: `ListingsMap` resolves
 * the popover's REFERENCE ELEMENT by this very id
 * (`pillRef={selectedId === listing.id ? ... : undefined}`), so an open preview
 * whose pin stops being the highlighted one loses the element Floating UI is
 * positioned against, and the popover comes down. A card highlight that
 * displaced this would therefore close the popover through the map's own
 * wiring even with the two atoms separate - the same defect, one layer down.
 * The marker wins while it exists; the card highlight is what the map shows the
 * rest of the time.
 */
export function highlightedPinId(
  markerPreview: ListingCardData | null,
  highlightedCardId: ListingId | null
): ListingId | null {
  return markerPreview === null ? highlightedCardId : markerPreview.id;
}

/**
 * Whether the mobile results drawer is on screen - DERIVED, never stored.
 *
 * The drawer leaves the screen for exactly one reason: the marker preview card
 * takes the bottom edge, and `ListingsClient` renders that card from the SAME
 * value this reads. So the drawer's openness is a pure function of it,
 * evaluated in the render that decides whether the preview is painted at all,
 * and "hidden with nothing on screen" is not a state anything can hold.
 *
 * What this REPLACES is a stored `{ kind: 'hiddenForPreview' }` in a reducer a
 * DESKTOP pin click could drive - the drawer is `md:hidden` and unmounted up
 * there, so the state machine was mutable while its view did not exist, and
 * every event that would have corrected it belonged to a surface that was not
 * rendered. A derived fact cannot be driven somewhere stale by a surface that
 * is not on screen, because there is nothing to drive.
 */
export function resultsSheetIsOpen(markerPreview: ListingCardData | null): boolean {
  return markerPreview === null;
}
